import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Search, Plus, Users, ShieldCheck, MapPin, Activity, MoreHorizontal,
  UserCog, KeyRound, Ban, CheckCircle2, ArrowRight, ArrowLeft,
  X, Info, AlertTriangle, Copy, Check,
} from "lucide-react";

/* -------------------------------------------------- Helpers -------- */

const STATUS_COPY = {
  active: { label: "Active", tone: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  inactive: { label: "Inactive", tone: "bg-foreground/[0.06] ink-muted border-border" },
};

function statusFor(m) { return m.active ? "active" : "inactive"; }
function initialsFor(name, email) {
  const src = (name || email || "?").trim();
  return src.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}
function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
}
function branchLabel(m, branchesById) {
  if (m.all_branches) return "All branches";
  const ids = m.branch_ids || [];
  if (ids.length === 0) return "No branches";
  if (ids.length === 1) return branchesById[ids[0]]?.name || "1 branch";
  return `${ids.length} branches`;
}

/* -------------------------------------------------- Page ----------- */

export default function TeamPage() {
  const { user, activeMembership } = useAuth();
  const [tab, setTab] = useState("users");
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null); // membership id
  const [tempCred, setTempCred] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, b] = await Promise.all([
        api.get("/users"),
        api.get("/roles"),
        api.get("/branches"),
      ]);
      setMembers(u.data); setRoles(r.data); setBranches(b.data);
    } catch (e) { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const branchesById = useMemo(() =>
    Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  const stats = useMemo(() => ({
    active: members.filter((m) => m.active).length,
    inactive: members.filter((m) => !m.active).length,
    totalRoles: roles.length,
    totalBranches: branches.length,
  }), [members, roles, branches]);

  const detailMember = useMemo(() =>
    members.find((m) => m.membership_id === detail) || null, [members, detail]);

  return (
    <div className="h-full w-full overflow-y-auto" data-testid="team-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="section-eyebrow flex items-center gap-2">
              <span className="section-num">.16</span>
              <span className="h-px w-6 bg-foreground/25" />
              Team & Access
            </div>
            <h1 className="mt-2 display-heading text-3xl md:text-4xl ink leading-none tracking-tight">
              Users, roles &amp; permissions
            </h1>
            <p className="mt-2 text-[12.5px] ink-muted">
              Manage who has access to this organization, their roles, and their branch scope.
            </p>
          </div>
          {tab === "users" && (
            <button
              onClick={() => setShowAdd(true)}
              data-testid="team-add-user-btn"
              className="h-10 px-4 rounded-lg bg-foreground text-background text-[12.5px] font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <Plus size={14} /> Add User
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Active users"   value={stats.active}   icon={CheckCircle2} />
          <StatCard label="Inactive"       value={stats.inactive} icon={Ban} />
          <StatCard label="Roles"          value={stats.totalRoles}  icon={ShieldCheck} />
          <StatCard label="Branches"       value={stats.totalBranches} icon={MapPin} />
        </div>

        {/* Tabs */}
        <div className="border-b hairline flex items-center gap-1" role="tablist">
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={Users} label="Users" testId="team-tab-users" />
          <TabBtn active={tab === "roles"} onClick={() => setTab("roles")} icon={ShieldCheck} label="Roles & Permissions" testId="team-tab-roles" />
          <TabBtn active={tab === "branches"} onClick={() => setTab("branches")} icon={MapPin} label="Branch Access" testId="team-tab-branches" />
        </div>

        {/* Panels */}
        {tab === "users" && (
          <UsersPanel
            members={members} roles={roles} branches={branches}
            branchesById={branchesById} loading={loading}
            currentUserId={user?.id}
            onOpenDetail={setDetail}
          />
        )}
        {tab === "roles" && <RolesPanel roles={roles} members={members} />}
        {tab === "branches" && <BranchesPanel members={members} branches={branches} onOpenDetail={setDetail} />}
      </div>

      {showAdd && (
        <AddUserModal
          roles={roles} branches={branches}
          onClose={() => setShowAdd(false)}
          onCreated={(payload) => {
            setShowAdd(false);
            if (payload?.temp_password) setTempCred(payload);
            load();
          }}
        />
      )}

      {detailMember && (
        <UserDetailDrawer
          member={detailMember}
          roles={roles} branches={branches}
          currentUserId={user?.id}
          onClose={() => setDetail(null)}
          onSaved={() => { load(); }}
        />
      )}

      {tempCred && <TempPasswordModal payload={tempCred} onClose={() => setTempCred(null)} />}
    </div>
  );
}

/* -------------------------------------------------- Header widgets - */

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border hairline surface-card px-4 py-3 flex items-center gap-3" data-testid={`team-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="h-9 w-9 grid place-items-center rounded-md bg-foreground/[0.06]">
        <Icon size={15} className="ink" />
      </span>
      <div className="leading-tight">
        <div className="tiny-label">{label}</div>
        <div className="mt-0.5 text-lg font-semibold ink mono">{value}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={`relative h-10 px-4 flex items-center gap-2 text-[12.5px] font-medium transition-colors ${
        active ? "ink" : "ink-muted hover:ink"
      }`}
    >
      <Icon size={13} />
      <span>{label}</span>
      {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-foreground" />}
    </button>
  );
}

/* -------------------------------------------------- Users Panel ---- */

function UsersPanel({ members, roles, branches, branchesById, loading, currentUserId, onOpenDetail }) {
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return members.filter((m) => {
      if (needle && !(m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle))) return false;
      if (roleFilter !== "all" && m.role !== roleFilter) return false;
      if (statusFilter !== "all" && statusFor(m) !== statusFilter) return false;
      return true;
    });
  }, [members, q, roleFilter, statusFilter]);

  return (
    <div className="space-y-3" data-testid="team-users-panel">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-[380px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 ink-subtle" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="team-users-search"
            className="h-9 w-full rounded-lg bg-foreground/[0.03] border hairline pl-8 pr-3 text-[12.5px] ink placeholder:ink-subtle focus:outline-none focus:border-foreground/30 transition-colors"
          />
        </div>
        <Select value={roleFilter} onChange={setRoleFilter} testId="team-users-role-filter">
          <option value="all">All roles</option>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </Select>
        <Select value={statusFilter} onChange={setStatusFilter} testId="team-users-status-filter">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
        {(q || roleFilter !== "all" || statusFilter !== "all") && (
          <button
            className="h-9 px-3 rounded-lg text-[11.5px] ink-muted hover:ink hover:bg-foreground/[0.05] transition-colors"
            onClick={() => { setQ(""); setRoleFilter("all"); setStatusFilter("all"); }}
            data-testid="team-users-reset-filters"
          >
            Reset
          </button>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-lg border hairline surface-card overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left border-b hairline">
              <th className="px-4 py-3 tiny-label">User</th>
              <th className="px-4 py-3 tiny-label">Role</th>
              <th className="px-4 py-3 tiny-label">Branch access</th>
              <th className="px-4 py-3 tiny-label">Status</th>
              <th className="px-4 py-3 tiny-label">Last login</th>
              <th className="px-4 py-3 tiny-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center ink-subtle text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center">
                <div className="ink-muted text-xs">No users match the current filters.</div>
              </td></tr>
            )}
            {filtered.map((m) => (
              <tr key={m.membership_id} className="border-b hairline-soft last:border-none hover:bg-foreground/[0.02] transition-colors cursor-pointer"
                  data-testid={`team-row-${m.user_id}`}
                  onClick={() => onOpenDetail(m.membership_id)}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[10.5px] font-semibold">
                      {initialsFor(m.name, m.email)}
                    </span>
                    <div className="leading-tight min-w-0">
                      <div className="font-semibold ink truncate">
                        {m.name || "—"}
                        {m.user_id === currentUserId && <span className="ml-2 text-[10px] ink-subtle font-normal">(You)</span>}
                      </div>
                      <div className="text-[11px] ink-muted truncate">{m.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 ink">{m.role_label}</td>
                <td className="px-4 py-3 ink-muted">{branchLabel(m, branchesById)}</td>
                <td className="px-4 py-3"><StatusBadge status={statusFor(m)} /></td>
                <td className="px-4 py-3 ink-subtle text-[11px]">{formatDate(m.last_login)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    className="h-8 w-8 grid place-items-center rounded-md ink-muted hover:ink hover:bg-foreground/[0.05] transition-colors"
                    onClick={(e) => { e.stopPropagation(); onOpenDetail(m.membership_id); }}
                    aria-label="Open user details"
                    data-testid={`team-row-actions-${m.user_id}`}
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {filtered.map((m) => (
          <button key={m.membership_id}
                  onClick={() => onOpenDetail(m.membership_id)}
                  data-testid={`team-card-${m.user_id}`}
                  className="w-full text-left rounded-lg border hairline surface-card p-3 flex items-center gap-3 hover:border-foreground/30 transition-colors">
            <span className="h-9 w-9 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[11px] font-semibold shrink-0">
              {initialsFor(m.name, m.email)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold ink text-[13px] truncate">{m.name || "—"}</div>
              <div className="text-[11px] ink-muted truncate">{m.email}</div>
              <div className="mt-1 flex items-center gap-2 text-[10.5px] ink-subtle">
                <span>{m.role_label}</span>
                <span>·</span>
                <span>{branchLabel(m, branchesById)}</span>
              </div>
            </div>
            <StatusBadge status={statusFor(m)} />
          </button>
        ))}
        {!loading && filtered.length === 0 && (
          <div className="rounded-lg border hairline surface-card p-6 text-center ink-muted text-xs">No users match.</div>
        )}
      </div>
    </div>
  );
}

function Select({ value, onChange, testId, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-9 rounded-lg bg-foreground/[0.03] border hairline px-3 text-[12.5px] ink focus:outline-none focus:border-foreground/30"
    >
      {children}
    </select>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_COPY[status] || STATUS_COPY.inactive;
  return (
    <span className={`inline-flex items-center h-6 px-2 rounded-md border text-[10.5px] font-semibold ${s.tone}`}>
      {s.label}
    </span>
  );
}

/* -------------------------------------------------- Roles panel --- */

const PERMISSION_CATEGORIES = [
  { key: "pos", label: "POS & Orders", match: /^(order|payment|refund|discount|kds|table)\./ },
  { key: "reservations", label: "Reservations & Guests", match: /^(reservation|guest|guest_list|foot_traffic)\./ },
  { key: "events", label: "Events & Promoters", match: /^(event|promoter)\./ },
  { key: "inventory", label: "Inventory & Purchasing", match: /^(inventory|purchasing|supplier|product)\./ },
  { key: "entrance", label: "Entrance & Security", match: /^(entrance|id_verification|facility)\./ },
  { key: "finance", label: "Finance & Cash", match: /^(cash_session|approval)\./ },
  { key: "payroll", label: "Employees & Payroll", match: /^(attendance|payroll|payslip)\./ },
  { key: "marketing", label: "Marketing", match: /^marketing\./ },
  { key: "operations", label: "Operations", match: /^operations\./ },
  { key: "users", label: "User & Access Management", match: /^(user|branch)\./ },
  { key: "settings", label: "Settings & Audit", match: /^(settings|audit_log|dashboard)\./ },
];

const PERMISSION_LABELS = {
  "dashboard.view": "View dashboards & reports",
  "branch.manage": "Manage branches",
  "user.manage": "Manage users & roles",
  "product.manage": "Manage catalog & products",
  "order.view": "View orders",
  "order.create": "Create orders",
  "order.modify": "Modify orders",
  "order.void": "Void orders",
  "payment.process": "Process payments",
  "refund.request": "Request refunds",
  "refund.approve": "Approve refunds",
  "discount.apply": "Apply discounts",
  "discount.approve": "Approve discounts",
  "inventory.view": "View inventory",
  "inventory.adjust": "Adjust inventory",
  "reservation.manage": "Manage reservations",
  "reservation.open_pos_tab": "Open POS tab from a reservation",
  "event.manage": "Manage events",
  "guest_list.manage": "Manage guest lists",
  "entrance.scan": "Scan entrance QR",
  "entrance.override": "Override entrance rules",
  "entrance.session.manage": "Manage entrance sessions",
  "id_verification.perform": "Verify guest IDs",
  "foot_traffic.view": "View foot traffic",
  "attendance.manage": "Manage attendance",
  "attendance.own": "Log own attendance",
  "payroll.view": "View payroll",
  "payroll.prepare": "Prepare pay runs",
  "payslip.view_own": "View own payslip",
  "cash_session.manage": "Manage cash sessions",
  "approval.manage": "Manage approvals",
  "settings.manage": "Manage settings",
  "audit_log.view": "View audit log",
  "kds.view": "View kitchen display",
  "kds.update": "Update kitchen tickets",
  "table.manage": "Manage tables & floors",
  "table.status_update": "Update table status",
  "promoter.commission.view": "View promoter commissions",
  "promoter.commission.manage": "Manage promoter commissions",
  "promoter.payout.view": "View promoter payouts",
  "promoter.payout.manage": "Manage promoter payouts",
};
const SENSITIVE_PERMS = new Set([
  "user.manage", "branch.manage", "settings.manage", "order.void",
  "refund.approve", "discount.approve", "approval.manage",
  "audit_log.view", "promoter.payout.manage",
]);

function permLabel(p) {
  return PERMISSION_LABELS[p] || p.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function categorize(perms) {
  const buckets = new Map(PERMISSION_CATEGORIES.map((c) => [c.key, []]));
  const other = [];
  for (const p of perms) {
    const cat = PERMISSION_CATEGORIES.find((c) => c.match.test(p));
    if (cat) buckets.get(cat.key).push(p);
    else other.push(p);
  }
  return { buckets, other };
}

function RolesPanel({ roles, members }) {
  const [selected, setSelected] = useState(roles[0]?.key || null);
  useEffect(() => { if (!selected && roles[0]) setSelected(roles[0].key); }, [roles, selected]);
  const active = roles.find((r) => r.key === selected);
  const usage = useMemo(() => {
    const m = new Map();
    for (const mem of members) m.set(mem.role, (m.get(mem.role) || 0) + 1);
    return m;
  }, [members]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="team-roles-panel">
      <div className="rounded-lg border hairline surface-card p-2 max-h-[70vh] overflow-y-auto">
        {roles.map((r) => (
          <button key={r.key} onClick={() => setSelected(r.key)}
            data-testid={`role-list-${r.key}`}
            className={`w-full text-left px-3 py-2.5 rounded-md text-[12.5px] transition-colors ${
              selected === r.key ? "bg-primary/10 ink" : "ink hover:bg-foreground/[0.05]"
            }`}>
            <div className="flex items-center justify-between">
              <span className="font-medium truncate">{r.label}</span>
              <span className="text-[10px] ink-subtle mono">{usage.get(r.key) || 0}</span>
            </div>
            <div className="text-[10px] ink-subtle truncate mt-0.5">{r.permissions.length} permissions</div>
          </button>
        ))}
      </div>

      {active && (
        <div className="rounded-lg border hairline surface-card p-5 space-y-4" data-testid={`role-detail-${active.key}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="tiny-label">Role</div>
              <h3 className="mt-1 text-xl font-bold ink tracking-tight">{active.label}</h3>
              <p className="mt-1 text-[12px] ink-muted">{ROLE_DESCRIPTIONS[active.key] || "Predefined system role."}</p>
              <div className="mt-2 flex items-center gap-3 text-[10.5px] ink-subtle">
                <span>{usage.get(active.key) || 0} assigned users</span>
                <span>·</span>
                <span>{active.permissions.length} permissions</span>
              </div>
            </div>
            <div className="text-[10px] ink-subtle text-right">
              <div>System role</div>
              <div className="mt-1">Read-only</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PERMISSION_CATEGORIES.map((cat) => {
              const perms = active.permissions.filter((p) => cat.match.test(p));
              if (perms.length === 0) return null;
              return (
                <div key={cat.key} className="rounded-md border hairline-soft bg-foreground/[0.02] p-3">
                  <div className="tiny-label mb-2">{cat.label}</div>
                  <ul className="space-y-1">
                    {perms.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-[11.5px] ink">
                        <Check size={11} className="mt-0.5 text-emerald-400 shrink-0" />
                        <span>{permLabel(p)}</span>
                        {SENSITIVE_PERMS.has(p) && (
                          <AlertTriangle size={10} className="text-amber-400 shrink-0 mt-0.5" title="Sensitive permission" />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const ROLE_DESCRIPTIONS = {
  business_owner: "Full control of the organization, including finance and access.",
  general_manager: "Cross-branch operator with broad management authority.",
  branch_manager: "Manages daily branch operations, staff, and reports.",
  cashier: "Processes orders and payments through the POS.",
  server: "Takes orders and updates tables from the floor.",
  bartender: "Fulfills drink tickets from the bar station.",
  kitchen_staff: "Fulfills food tickets from the kitchen display.",
  inventory_controller: "Manages stock, adjustments, and purchasing lanes.",
  host: "Runs check-in queue, seating, and VIP notes.",
  event_manager: "Runs events, promoters, and guest-list operations.",
  promoter: "Sells tickets and manages personal guest lists.",
  security_personnel: "Runs the door — scan, ID checks, incident intake.",
  hr_payroll_officer: "Manages employees, attendance, and payroll runs.",
  employee: "Self-service — attendance and own payslip only.",
  read_only_viewer: "Read-only access to dashboards and reports.",
};

/* -------------------------------------------------- Branches panel  */

function BranchesPanel({ members, branches, onOpenDetail }) {
  return (
    <div className="space-y-3" data-testid="team-branches-panel">
      {branches.map((b) => {
        const active = members.filter((m) => m.all_branches || (m.branch_ids || []).includes(b.id));
        return (
          <div key={b.id} className="rounded-lg border hairline surface-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold ink">{b.name}</div>
                <div className="text-[11px] ink-subtle mono">{b.code}</div>
              </div>
              <div className="text-[11px] ink-muted">{active.length} user{active.length === 1 ? "" : "s"} with access</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {active.map((m) => (
                <button key={m.membership_id}
                  onClick={() => onOpenDetail(m.membership_id)}
                  data-testid={`branch-user-${b.id}-${m.user_id}`}
                  className="inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full border hairline surface-card hover:border-foreground/30 transition-colors">
                  <span className="h-5 w-5 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[9px] font-semibold">
                    {initialsFor(m.name, m.email)}
                  </span>
                  <span className="text-[11px] ink">{m.name || m.email}</span>
                  {m.all_branches && <span className="text-[9px] ink-subtle uppercase tracking-wider">all</span>}
                </button>
              ))}
              {active.length === 0 && <div className="text-[11.5px] ink-muted">No users assigned yet.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------- Add User modal  */

const ADD_STEPS = ["Profile", "Access", "Role", "Review"];

function AddUserModal({ roles, branches, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", email: "", role: "cashier", all_branches: false, branch_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const canNext = () => {
    if (step === 0) return form.name.trim().length > 1 && /.+@.+\..+/.test(form.email);
    if (step === 1) return form.all_branches || form.branch_ids.length > 0;
    if (step === 2) return !!form.role;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/users/invite", form);
      toast.success(`Invited ${form.email}`);
      onCreated({ ...data, email: form.email, name: form.name });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to invite user");
    } finally { setSubmitting(false); }
  };

  const roleMeta = roles.find((r) => r.key === form.role);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="add-user-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={submitting ? undefined : onClose} />
      <div className="relative w-full max-w-lg rounded-xl border hairline bg-popover text-popover-foreground shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b hairline flex items-center justify-between">
          <div>
            <div className="tiny-label">New Team Member</div>
            <div className="text-[15px] font-semibold ink mt-0.5">Add User</div>
          </div>
          <button onClick={onClose} disabled={submitting} className="ink-muted hover:ink" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 pt-4">
          <div className="flex items-center gap-2">
            {ADD_STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-1.5 text-[11px] ${i === step ? "ink" : "ink-subtle"}`}>
                  <span className={`h-5 w-5 grid place-items-center rounded-full text-[10px] font-semibold ${
                    i < step ? "bg-emerald-500 text-white" : i === step ? "bg-foreground text-background" : "bg-foreground/[0.08] ink-muted"
                  }`}>
                    {i < step ? <Check size={11} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < ADD_STEPS.length - 1 && <span className="flex-1 h-px bg-border" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {step === 0 && (
            <>
              <Field label="Full name" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="add-user-name" placeholder="e.g. Selena Cruz"
                  className="fld" autoFocus />
              </Field>
              <Field label="Email" required>
                <input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="add-user-email" placeholder="name@company.com" className="fld" />
              </Field>
              <div className="rounded-md bg-foreground/[0.04] p-3 flex gap-2 items-start">
                <Info size={13} className="ink-subtle mt-0.5" />
                <p className="text-[11.5px] ink-muted leading-snug">
                  We'll send an invite email. The user sets their own password from the link — you never see it.
                </p>
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <label className="flex items-center gap-2 p-3 rounded-md border hairline hover:bg-foreground/[0.03] transition-colors cursor-pointer">
                <input type="checkbox" checked={form.all_branches}
                  onChange={(e) => setForm({ ...form, all_branches: e.target.checked, branch_ids: e.target.checked ? [] : form.branch_ids })}
                  data-testid="add-user-all-branches" />
                <div className="flex-1">
                  <div className="text-[12.5px] font-medium ink">Grant access to all branches</div>
                  <div className="text-[11px] ink-muted">Use for owners and org-wide managers.</div>
                </div>
              </label>
              {!form.all_branches && (
                <>
                  <div className="tiny-label mt-2">Specific branches</div>
                  <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                    {branches.map((b) => (
                      <label key={b.id}
                        className="flex items-center gap-2 p-2.5 rounded-md border hairline-soft hover:bg-foreground/[0.03] cursor-pointer">
                        <input type="checkbox"
                          checked={form.branch_ids.includes(b.id)}
                          data-testid={`add-user-branch-${b.id}`}
                          onChange={(e) => {
                            const s = new Set(form.branch_ids);
                            e.target.checked ? s.add(b.id) : s.delete(b.id);
                            setForm({ ...form, branch_ids: [...s] });
                          }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] ink truncate">{b.name}</div>
                          <div className="text-[10px] ink-subtle mono truncate">{b.code}</div>
                        </div>
                      </label>
                    ))}
                    {branches.length === 0 && <div className="text-[11.5px] ink-muted p-2">No branches available.</div>}
                  </div>
                </>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <div className="tiny-label">Choose a role</div>
              <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                {roles.map((r) => (
                  <label key={r.key}
                    className={`block p-3 rounded-md border cursor-pointer transition-colors ${
                      form.role === r.key ? "bg-primary/10 border-primary/40 ink" : "hairline hover:bg-foreground/[0.03]"
                    }`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="role" value={r.key} checked={form.role === r.key}
                        onChange={() => setForm({ ...form, role: r.key })}
                        data-testid={`add-user-role-${r.key}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-semibold ink">{r.label}</div>
                        <div className="text-[11px] ink-muted">{ROLE_DESCRIPTIONS[r.key] || `${r.permissions.length} permissions`}</div>
                      </div>
                      <span className="text-[10px] ink-subtle mono shrink-0">{r.permissions.length} perms</span>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <ReviewList items={[
              ["Name", form.name],
              ["Email", form.email],
              ["Role", roleMeta?.label || form.role],
              ["Branch access", form.all_branches ? "All branches" : `${form.branch_ids.length} branch(es)`],
              ["Account status", "Active (invitation sent)"],
            ]} />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-between gap-2 surface-2">
          <button onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} disabled={submitting}
            className="h-9 px-3 rounded-lg text-[12px] ink-muted hover:ink hover:bg-foreground/[0.05] flex items-center gap-1.5">
            {step === 0 ? "Cancel" : <><ArrowLeft size={12} /> Back</>}
          </button>
          {step < ADD_STEPS.length - 1 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canNext()}
              data-testid="add-user-next"
              className="h-9 px-4 rounded-lg bg-foreground text-background text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40 hover:opacity-90">
              Next <ArrowRight size={12} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting}
              data-testid="add-user-submit"
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40 hover:opacity-90">
              {submitting ? "Creating…" : "Create User"}
            </button>
          )}
        </div>
      </div>
      <style>{`.fld{margin-top:6px;height:38px;width:100%;border-radius:8px;background:hsl(var(--background));border:1px solid hsl(var(--border));padding:0 12px;color:hsl(var(--foreground));font-size:13px;outline:none;transition:border-color .15s} .fld:focus{border-color:hsl(var(--primary))}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="tiny-label flex items-center gap-1">
        {label} {required && <span className="text-red-400">*</span>}
      </div>
      {children}
    </label>
  );
}
function ReviewList({ items }) {
  return (
    <div className="rounded-md border hairline surface-card divide-y divide-border" data-testid="add-user-review">
      {items.map(([k, v]) => (
        <div key={k} className="px-3 py-2.5 flex items-start justify-between gap-3">
          <span className="text-[11px] ink-muted">{k}</span>
          <span className="text-[12.5px] ink font-medium text-right">{v || "—"}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------- Temp password - */

function TempPasswordModal({ payload, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(payload.temp_password || "");
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" data-testid="temp-password-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border hairline bg-popover text-popover-foreground shadow-2xl">
        <div className="px-5 py-4 border-b hairline">
          <div className="tiny-label">One-Time Credential</div>
          <div className="text-[15px] font-semibold ink mt-0.5">Temporary password</div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] ink-muted leading-snug">
            Share this ONCE with <span className="ink">{payload.email}</span>. They'll be required to change it on first
            sign-in. We will never show this password again.
          </p>
          <div className="rounded-md bg-foreground/[0.05] px-3 py-3 flex items-center gap-2">
            <code className="flex-1 font-mono text-[13px] ink break-all">{payload.temp_password}</code>
            <button onClick={copy} className="h-8 px-2.5 rounded-md bg-foreground text-background text-[11px] font-semibold flex items-center gap-1.5">
              {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
            </button>
          </div>
          <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3 flex items-start gap-2">
            <AlertTriangle size={13} className="text-amber-400 mt-0.5" />
            <p className="text-[11px] ink-muted">Copy now — this dialog will not reappear.</p>
          </div>
        </div>
        <div className="px-5 py-3 border-t hairline flex justify-end">
          <button onClick={onClose} data-testid="temp-password-close"
            className="h-9 px-4 rounded-lg bg-foreground text-background text-[12.5px] font-semibold">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- User detail --- */

function UserDetailDrawer({ member, roles, branches, currentUserId, onClose, onSaved }) {
  const isSelf = member.user_id === currentUserId;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    role: member.role,
    all_branches: member.all_branches,
    branch_ids: [...(member.branch_ids || [])],
    active: member.active,
  });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const dirty = form.role !== member.role
    || form.all_branches !== member.all_branches
    || JSON.stringify([...form.branch_ids].sort()) !== JSON.stringify([...(member.branch_ids || [])].sort())
    || form.active !== member.active;

  const doSave = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/users/${member.membership_id}`, payload);
      toast.success("Changes saved");
      onSaved();
      setEditing(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" && detail?.message) || formatApiError(detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const save = () => {
    const payload = {};
    if (form.role !== member.role) payload.role = form.role;
    if (form.all_branches !== member.all_branches) payload.all_branches = form.all_branches;
    if (JSON.stringify([...form.branch_ids].sort()) !== JSON.stringify([...(member.branch_ids || [])].sort())) {
      payload.branch_ids = form.branch_ids;
    }
    if (form.active !== member.active) payload.active = form.active;

    const sensitive = "role" in payload || "active" in payload;
    if (sensitive) {
      setConfirm({
        title: form.active !== member.active ? (form.active ? "Reactivate this user?" : "Deactivate this user?") : "Change this user's role?",
        message: form.active !== member.active
          ? (form.active
              ? "The user will regain access based on their role and branches."
              : "The user will immediately lose access. This can be reversed later.")
          : `Change ${member.name || member.email}'s role from ${member.role_label} to ${roles.find((r)=>r.key===form.role)?.label || form.role}? Access changes take effect immediately.`,
        onConfirm: () => { setConfirm(null); doSave(payload); },
      });
    } else {
      doSave(payload);
    }
  };

  const sendReset = async () => {
    try {
      const { data } = await api.post(`/users/${member.membership_id}/send-password-reset`);
      toast.success(`Password reset link sent to ${data.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send reset link");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid="user-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative ml-auto w-full max-w-[520px] h-full surface flex flex-col shadow-2xl border-l hairline">
        {/* Header */}
        <div className="px-5 py-4 border-b hairline flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-10 w-10 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[13px] font-semibold shrink-0">
              {initialsFor(member.name, member.email)}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold ink truncate">
                {member.name || "—"}
                {isSelf && <span className="ml-2 text-[10px] ink-subtle font-normal">(You)</span>}
              </div>
              <div className="text-[11px] ink-muted truncate">{member.email}</div>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={statusFor(member)} />
                <span className="text-[10.5px] ink-subtle">{member.role_label}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="ink-muted hover:ink" aria-label="Close" data-testid="user-detail-close">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Profile */}
          <Section title="Profile">
            <KV label="Full name">{member.name || "—"}</KV>
            <KV label="Email">{member.email}</KV>
            <KV label="Account status">
              <StatusBadge status={statusFor(member)} />
            </KV>
          </Section>

          {/* Access */}
          <Section title="Access" action={!editing && (
            <button onClick={() => setEditing(true)} data-testid="user-detail-edit-access"
              className="text-[11px] font-semibold ink-muted hover:ink flex items-center gap-1">
              <UserCog size={11} /> Edit access
            </button>
          )}>
            {!editing ? (
              <>
                <KV label="Role">{member.role_label}</KV>
                <KV label="Branch access">
                  {member.all_branches ? "All branches" : (member.branch_ids || []).length === 0
                    ? "No branches"
                    : (member.branch_ids || []).map((id) => branches.find((b) => b.id === id)?.name || id).join(", ")}
                </KV>
              </>
            ) : (
              <div className="space-y-3">
                <Field label="Role">
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    data-testid="user-detail-role-select" className="fld" disabled={isSelf}>
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                  {isSelf && <div className="mt-1 text-[10.5px] ink-subtle">You cannot change your own role.</div>}
                </Field>
                <label className="flex items-center gap-2 p-2.5 rounded-md border hairline">
                  <input type="checkbox" checked={form.all_branches}
                    data-testid="user-detail-all-branches"
                    onChange={(e) => setForm({ ...form, all_branches: e.target.checked, branch_ids: e.target.checked ? [] : form.branch_ids })} />
                  <span className="text-[12.5px] ink">Access to all branches</span>
                </label>
                {!form.all_branches && (
                  <div className="max-h-[220px] overflow-y-auto space-y-1 pr-1">
                    {branches.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-foreground/[0.03]">
                        <input type="checkbox"
                          checked={form.branch_ids.includes(b.id)}
                          data-testid={`user-detail-branch-${b.id}`}
                          onChange={(e) => {
                            const s = new Set(form.branch_ids);
                            e.target.checked ? s.add(b.id) : s.delete(b.id);
                            setForm({ ...form, branch_ids: [...s] });
                          }} />
                        <span className="text-[12px] ink">{b.name}</span>
                        <span className="ml-auto text-[10px] ink-subtle mono">{b.code}</span>
                      </label>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-between gap-2 p-2.5 rounded-md border hairline">
                  <span className="text-[12.5px] ink">Account active</span>
                  <input type="checkbox" checked={form.active}
                    disabled={isSelf}
                    data-testid="user-detail-active"
                    onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                </label>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => { setEditing(false); setForm({
                    role: member.role, all_branches: member.all_branches,
                    branch_ids: [...(member.branch_ids || [])], active: member.active,
                  }); }}
                    className="h-9 px-3 rounded-lg text-[12px] ink-muted hover:ink hover:bg-foreground/[0.05]">
                    Cancel
                  </button>
                  <button onClick={save} disabled={!dirty || saving}
                    data-testid="user-detail-save"
                    className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-semibold disabled:opacity-40">
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            )}
          </Section>

          {/* Security */}
          <Section title="Security">
            <button onClick={sendReset} data-testid="user-detail-send-reset"
              className="w-full h-10 rounded-lg border hairline surface-card hover:border-foreground/30 flex items-center justify-center gap-2 text-[12.5px] ink font-medium">
              <KeyRound size={13} /> Send password reset email
            </button>
            <p className="mt-2 text-[10.5px] ink-subtle">
              A secure single-use reset link (24h) will be emailed to {member.email}. We never display or store passwords in plain text.
            </p>
          </Section>

          {/* Activity */}
          <Section title="Activity">
            <KV label="Last login">{formatDate(member.last_login)}</KV>
          </Section>
        </div>
      </div>

      {confirm && <ConfirmDialog title={confirm.title} message={confirm.message}
        onCancel={() => setConfirm(null)} onConfirm={confirm.onConfirm} />}

      <style>{`.fld{margin-top:6px;height:38px;width:100%;border-radius:8px;background:hsl(var(--background));border:1px solid hsl(var(--border));padding:0 12px;color:hsl(var(--foreground));font-size:13px;outline:none} .fld:focus{border-color:hsl(var(--primary))}`}</style>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="tiny-label">{title}</div>
        {action}
      </div>
      <div className="rounded-lg border hairline surface-card divide-y divide-border">{children}</div>
    </section>
  );
}
function KV({ label, children }) {
  return (
    <div className="px-3.5 py-2.5 flex items-start justify-between gap-3">
      <span className="text-[11px] ink-muted">{label}</span>
      <span className="text-[12.5px] ink text-right max-w-[70%]">{children}</span>
    </div>
  );
}

function ConfirmDialog({ title, message, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="confirm-dialog">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div className="relative w-full max-w-sm rounded-xl border hairline bg-popover text-popover-foreground shadow-2xl">
        <div className="p-5">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 grid place-items-center rounded-full bg-amber-500/10">
              <AlertTriangle size={16} className="text-amber-400" />
            </span>
            <div>
              <div className="text-[14px] font-semibold ink">{title}</div>
              <p className="mt-1 text-[12px] ink-muted leading-snug">{message}</p>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2">
          <button onClick={onCancel} data-testid="confirm-cancel"
            className="h-9 px-3 rounded-lg text-[12px] ink-muted hover:ink hover:bg-foreground/[0.05]">
            Cancel
          </button>
          <button onClick={onConfirm} data-testid="confirm-proceed"
            className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-semibold">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
